import React, { useEffect, useState } from "react";
import StudentSidebar from "../common/studentSidebar";
import ProfileCard from "../common/profileCard";
import apiInstance from "../../../utility/apiInstance";
import { useSelector } from "react-redux";
import { ActivityLog } from "../../../core/types/types";

const actionConfig: Record<string, { icon: string; color: string; label: string }> = {
    // User account
    account_registered:           { icon: "fa-solid fa-user-plus",         color: "text-success",   label: "Account Registered" },
    profile_updated:              { icon: "fa-solid fa-user-pen",           color: "text-secondary", label: "Personal Profile Updated" },
    address_updated:              { icon: "fa-solid fa-location-dot",       color: "text-secondary", label: "Address Updated" },
    education_history_updated:    { icon: "fa-solid fa-graduation-cap",     color: "text-primary",   label: "Education History Updated" },
    work_experience_updated:      { icon: "fa-solid fa-briefcase",          color: "text-secondary", label: "Work Experience Updated" },
    test_scores_updated:          { icon: "fa-solid fa-star-half-stroke",   color: "text-warning",   label: "Test Scores Updated" },
    visa_updated:                 { icon: "fa-solid fa-passport",           color: "text-info",      label: "Visa & Study Permit Updated" },
    document_uploaded:            { icon: "fa-solid fa-file-arrow-up",      color: "text-success",   label: "Document Uploaded" },
    account_deleted:              { icon: "fa-solid fa-user-xmark",         color: "text-danger",    label: "Account Deleted" },
    // Applications
    application_submitted:        { icon: "fa-solid fa-paper-plane",        color: "text-primary",   label: "Application Submitted" },
    application_status_changed:   { icon: "fa-solid fa-arrows-rotate",      color: "text-info",      label: "Application Status Changed" },
    application_deleted:          { icon: "fa-solid fa-trash",              color: "text-danger",    label: "Application Deleted" },
    // Notes
    note_created:                 { icon: "fa-solid fa-note-sticky",        color: "text-success",   label: "Note Created" },
    note_updated:                 { icon: "fa-solid fa-pen",                color: "text-warning",   label: "Note Updated" },
    note_deleted:                 { icon: "fa-solid fa-trash",              color: "text-danger",    label: "Note Deleted" },
    // Security
    email_verified:               { icon: "fa-solid fa-envelope-circle-check", color: "text-success", label: "Email Verified" },
    password_changed:             { icon: "fa-solid fa-lock",               color: "text-warning",   label: "Password Changed" },
    recruiter_access:             { icon: "fa-solid fa-user-secret",        color: "text-warning",   label: "Recruiter Account Access" },
};

const getRelativeTime = (date: Date): string => {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return "";
    const diff = Date.now() - dateObj.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return dateObj.toLocaleDateString();
};

const StudentActivity = () => {
    const user = useSelector((state: any) => state.auth.user);

    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const fetchLogs = async () => {
            if (!user?.id) {
                setLoading(false);
                return;
            }
            try {
                const response = await apiInstance.get("/api/activityLogs", {
                    params: {
                        "where[user][equals]": user.id,
                        sort: "-createdAt",
                        limit: 50,
                    },
                });
                setLogs(response.data.docs);
            } catch (err) {
                console.error("Error fetching activity logs:", err);
                setError("Failed to load activity. Please refresh the page.");
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [user?.id]);

    return (
        <div className="content">
            <div className="container">
                <ProfileCard />
                <div className="row">
                    <StudentSidebar />
                    <div className="col-lg-9">

                        <div className="page-title d-flex align-items-center justify-content-between mb-4">
                            <h5 className="mb-0">Activity</h5>
                        </div>

                        {error && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
                                <i className="fa-solid fa-circle-exclamation" />
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="d-flex align-items-center gap-2 text-muted py-4">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Loading activity...
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                                <i className="fa-solid fa-clock-rotate-left mb-2" style={{ fontSize: "2.5rem", display: "block" }} />
                                <p className="mb-0">No activity yet.</p>
                            </div>
                        ) : (
                            <div className="card border shadow-sm">
                                <div className="card-body p-0">
                                    {logs.map((log: ActivityLog, index: number) => {
                                        const config = actionConfig[log.action] ?? {
                                            icon: "fa-solid fa-circle-dot",
                                            color: "text-muted",
                                            label: log.action,
                                        };
                                        return (
                                            <div
                                                key={log.id}
                                                className={`d-flex align-items-start gap-3 p-3 ${index < logs.length - 1 ? "border-bottom" : ""}`}
                                            >
                                                <span
                                                    className={`icon-box flex-shrink-0 ${config.color}`}
                                                    style={{ width: "2rem", height: "2rem", display: "flex", alignItems: "center", justifyContent: "center" }}
                                                >
                                                    <i className={config.icon} />
                                                </span>
                                                <div className="flex-grow-1 min-w-0">
                                                    <p className="mb-0 fw-medium small">{log.description || config.label}</p>
                                                    <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                        {getRelativeTime(log.createdAt)}
                                                        {" · "}
                                                        {new Date(log.createdAt).toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentActivity;
